import { FetchResult, gql, useMutation } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogTitle,
  Button,
  ButtonGroup,
} from '@apollo/orbit';
import React, { useCallback, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { ProposalSchemaInfo } from '../hooks/useProposalEditorState/useProposalEditorState';

export const DeleteProposalSubgraphModal = ({
  proposalId,
  isOpen,
  onClose,
  finalFocusRef,
  selectedSchemaInfo,
  setSubgraphsWithMetadata,
  setInitialLatestRevisionId,
  isStale,
  latestLaunchId,
}: {
  proposalId: string;
  isOpen: boolean;
  onClose: () => void;
  finalFocusRef?: React.RefObject<HTMLAnchorElement>;
  selectedSchemaInfo: ProposalSchemaInfo;
  setSubgraphsWithMetadata: (
    value: React.SetStateAction<[string, ProposalSchemaInfo][]>,
  ) => void;
  setInitialLatestRevisionId: (id: string) => void;
  isStale: boolean;
  latestLaunchId: string;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false);
  const { graphVariant } = useRouteParams(proposalEditorRouteConfig);
  const history = useHistory();
  const location = useLocation();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [deleteSubgraph] = useMutation<
    GraphQLTypes.DeleteProposalSubgraphMutation,
    GraphQLTypes.DeleteProposalSubgraphMutationVariables
  >(gql`
    mutation DeleteProposalSubgraphMutation(
      $proposalId: ID!
      $input: DeleteProposalSubgraphInput!
      $graphVariant: String!
    ) {
      proposal(id: $proposalId) {
        ... on ProposalMutation {
          deleteSubgraph(input: $input) {
            ... on PermissionError {
              message
            }
            ... on ValidationError {
              message
            }
            ... on Proposal {
              id
              latestRevision {
                id
              }
              backingVariant {
                id
                graph {
                  id
                  mostRecentCompositionPublish(graphVariant: $graphVariant) {
                    graphCompositionID
                  }
                }
                activeSchemaPublish {
                  id
                }
              }
            }
          }
        }
      }
    }
  `);

  const onCloseModal = useCallback(() => {
    onClose();
    setShouldShowErrorMessage(false);
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    let result:
      | FetchResult<
          GraphQLTypes.DeleteProposalSubgraphMutation,
          Record<string, unknown>,
          Record<string, unknown>
        >
      | undefined;

    if (typeof selectedSchemaInfo.initialText === 'string') {
      result = await deleteSubgraph({
        variables: {
          graphVariant,
          proposalId,
          input: {
            subgraphName: selectedSchemaInfo.id,
            summary: '',
            previousLaunchId: latestLaunchId,
          },
        },
      });

      const proposal = result.data?.proposal;
      if (proposal) {
        switch (proposal.__typename) {
          case 'NotFoundError':
          case 'PermissionError':
          case 'ValidationError':
            setShouldShowErrorMessage(true);
            break;
          case 'ProposalMutation':
            switch (proposal.deleteSubgraph.__typename) {
              case 'PermissionError':
              case 'ValidationError':
                setShouldShowErrorMessage(true);
                break;
              case 'Proposal': {
                setInitialLatestRevisionId(
                  proposal.deleteSubgraph.latestRevision.id,
                );
                break;
              }
              default:
                assertUnreachable(proposal.deleteSubgraph);
            }
            break;
          default:
            assertUnreachable(proposal);
        }
      }
    }

    if (!isMinLengthArray(1, result?.errors)) {
      setSubgraphsWithMetadata((currentValues) =>
        currentValues.filter(([name]) => name !== selectedSchemaInfo.id),
      );
      history.push(
        proposalEditorRouteConfig.locationFrom({
          location,
          patch: {
            selectedSchema: RouteConfig.DELETE,
          },
        }),
      );
      onCloseModal();
    } else {
      setShouldShowErrorMessage(true);
    }

    setIsSubmitting(false);
  }, [
    selectedSchemaInfo.initialText,
    selectedSchemaInfo.id,
    deleteSubgraph,
    graphVariant,
    proposalId,
    latestLaunchId,
    setInitialLatestRevisionId,
    setSubgraphsWithMetadata,
    history,
    location,
    onCloseModal,
  ]);

  return (
    <AlertDialog
      size="md"
      isOpen={isOpen}
      onClose={onCloseModal}
      finalFocusRef={finalFocusRef}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your subgraph?</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          {shouldShowErrorMessage && <ErrorMessage />}This action cannot be
          undone.
        </AlertDialogBody>
        <AlertDialogFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onCloseModal} ref={cancelRef}>
              Cancel
            </Button>
            <Tooltip
              disabled={!isStale}
              shouldWrapChildren={isStale}
              label="Cannot delete, local schema out of date"
            >
              <Button
                variant="destructive"
                type="submit"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Deleting"
                isDisabled={isStale}
              >
                Yes, delete
              </Button>
            </Tooltip>
          </ButtonGroup>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
